import {
  ConnectionType,
  intersectManySets,
  isMarketConfigured,
  isMarketOnline,
  logger,
  type Market,
  type MarketAccountsContextProps,
  MarketAccountTypeEnum,
  type MarketsContextProps,
  MarketTypeEnum,
  type OrderStrategy,
  PricingModeEnum,
  RFQTradingProtocolEnum,
  type SecuritiesContextProps,
  type Security,
  SecurityTypesUtils,
  type StrategiesContextProps,
  type UnifiedLiquidityResponse,
  useMarketAccountsContext,
  useMarketsContext,
  useSecuritiesContext,
  useStrategiesContext,
  useUnifiedLiquidity,
} from '@talos/kyoko';
import { compact, concat, uniq } from 'lodash-es';

import { type IFixingIndexContext, useFixingIndices } from '@src/providers/FixingIndexProvider';
import { useMemo } from 'react';
import { useMarketAccountPermissionFilters } from '../providers/MarketAccountPermissionFiltersProvider';
import { MULTILEG_MARKET_ACCOUNT } from '../providers/OMSContext.types';
import { useTradingSettings } from '../providers/TradingSettingsContext';
import type { TradingSettingsContextProps } from '../providers/TradingSettingsContext.types';
import { STRATEGY_GROUP } from '../tokens/order';
import { getRFQSyntheticCrossSymbol } from '../utils/rfq';
import { useFeatureFlag } from './useFeatureFlag';
import {
  getPermittedMarketAccountsSet,
  type GetPermittedMarketAccountsSetParams,
} from './usePermittedMarketAccountsSet';

export type UseTradableMarketAccountNamesParams = Pick<
  GetTradableMarketAccountNamesParams,
  | 'security'
  | 'customAllowedMarkets'
  | 'combineAllMarketAccounts'
  | 'includeOfflineMarkets'
  | 'connectionType'
  | 'allowSyntheticCrosses'
  | 'isUnifiedLiquidityEnabled'
  | 'strategy'
  | 'rfqTradingProtocol'
  | 'pricingMode'
  | 'fixingIndices'
  | 'usingCounterCurrency'
> &
  Partial<Pick<GetTradableMarketAccountNamesParams, 'subAccountAllocations'>>;

/**
 * Given a Security, strategy, configuration of the organizations PermissionFilters and subAccountAllocation(s),
 * returns list of configured, tradable market account names, independent of their status.
 */
export function useTradableMarketAccountNames({
  security,
  rfqTradingProtocol,
  pricingMode,
  fixingIndices,
  subAccountAllocations = -1,
  connectionType,
  isUnifiedLiquidityEnabled,
  combineAllMarketAccounts,
  strategy,
  usingCounterCurrency,
  includeOfflineMarkets,
  customAllowedMarkets,
}: UseTradableMarketAccountNamesParams): string[] {
  const ulResponse = useUnifiedLiquidity(security === 'custom' ? undefined : security?.Symbol);
  const { marketsByName } = useMarketsContext();

  const { marketAccountsByMarket } = useMarketAccountsContext();
  const { allowSyntheticCrosses, allowSyntheticCcy: allowSyntheticCrossCurrency } = useTradingSettings();
  const { securitiesBySymbol } = useSecuritiesContext();
  const { strategiesByName } = useStrategiesContext();
  const { marketsByFixingIndex } = useFixingIndices();

  const { enableAccountSegregation, showStFMarketsInNonStFNegotiatedRFQ } = useFeatureFlag();
  const { marketAccountsList } = useMarketAccountsContext();

  const { marketAccountFilterIDsBySubAccountName, marketAccountPermissionFiltersByFilterID } =
    useMarketAccountPermissionFilters();

  return useMemo(
    () =>
      getTradableMarketAccountNames({
        security,
        customAllowedMarkets,
        rfqTradingProtocol,
        pricingMode,
        fixingIndices,
        subAccountAllocations,
        connectionType,
        isUnifiedLiquidityEnabled,
        combineAllMarketAccounts,
        strategy,
        usingCounterCurrency,
        includeOfflineMarkets,
        showStFMarketsInNonStFNegotiatedRFQ,

        enableAccountSegregation,
        marketAccountsList,
        marketAccountFilterIDsBySubAccountName,
        marketAccountPermissionFiltersByFilterID,

        allowSyntheticCrosses,
        ulResponse,
        marketsByName,
        marketAccountsByMarket,
        allowSyntheticCrossCurrency,
        securitiesBySymbol,
        strategiesByName,
        marketsByFixingIndex,
      }),
    [
      security,
      customAllowedMarkets,
      rfqTradingProtocol,
      pricingMode,
      fixingIndices,
      subAccountAllocations,
      connectionType,
      isUnifiedLiquidityEnabled,
      combineAllMarketAccounts,
      strategy,
      usingCounterCurrency,
      includeOfflineMarkets,
      showStFMarketsInNonStFNegotiatedRFQ,

      enableAccountSegregation,
      marketAccountsList,
      marketAccountFilterIDsBySubAccountName,
      marketAccountPermissionFiltersByFilterID,

      allowSyntheticCrosses,
      ulResponse,
      marketsByName,
      marketAccountsByMarket,
      allowSyntheticCrossCurrency,
      securitiesBySymbol,
      strategiesByName,
      marketsByFixingIndex,
    ]
  );
}

type GetOrderMarketAccountNamesParams = {
  /** The security to grab Security.Markets from, or custom to use customAllowedMarkets. */
  security: Security | 'custom' | undefined;
  /** Market allow-list used when security is custom. */
  customAllowedMarkets?: string[];
  /** If provided, will filter to markets that support this strategy */
  strategy?: string;
  /** If provided, will only grab markets that work with this fixing index */
  fixingIndices?: string[];
  /** If RFQTradingProtocol = "Negotiated", only include market accounts that have the TradingRFQNegotiated capability */
  rfqTradingProtocol?: RFQTradingProtocolEnum;
  /** If provided, will filter to markets that support this pricing mode */
  pricingMode?: PricingModeEnum;
  /** When true, negotiated RFQs in price space keep markets that are also associated with a fixing index. */
  showStFMarketsInNonStFNegotiatedRFQ?: boolean;
  /**
   * Whether or not we are trading in counter currency. Some markets do not support this, and will be removed from the output if this is true.
   * Defaults to false.
   */
  usingCounterCurrency?: boolean;
  /** ConnectionType to use to filter out supported markets */
  connectionType: (typeof ConnectionType)['Orders' | 'RFQ' | 'MarketData'];
  /** Whether or not to include Accounts related to Markets where the Market has status Offline. Defaults to true. TODO: Find a way to remove this. */
  includeOfflineMarkets?: boolean;
  /** Only relevant for ConnectionType.Orders */
  isUnifiedLiquidityEnabled?: boolean;

  // Ref data below
  marketsByName: MarketsContextProps['marketsByName'];
  allowSyntheticCrosses?: TradingSettingsContextProps['allowSyntheticCrosses'];
  allowSyntheticCrossCurrency: TradingSettingsContextProps['allowSyntheticCcy'];
  securitiesBySymbol: SecuritiesContextProps['securitiesBySymbol'];
  ulResponse?: UnifiedLiquidityResponse;
  strategiesByName: StrategiesContextProps['strategiesByName'];
  marketsByFixingIndex: IFixingIndexContext['marketsByFixingIndex'];
  marketAccountsByMarket: MarketAccountsContextProps['marketAccountsByMarket'];
};

/**
 * Gets all the relevant order/rfq market account names given the parameters. Is simply a helper function for getTradableMarketAccountNames, and
 * does not do the trading account segregation check here (which would ensure that the accounts actually are tradable).
 */
function getOrderMarketAccountNames({
  security,
  customAllowedMarkets,
  includeOfflineMarkets,
  connectionType,
  allowSyntheticCrosses,
  isUnifiedLiquidityEnabled,
  strategy,
  rfqTradingProtocol,
  pricingMode,
  showStFMarketsInNonStFNegotiatedRFQ,
  fixingIndices,
  usingCounterCurrency,

  marketsByName,
  securitiesBySymbol,
  ulResponse,
  strategiesByName,
  marketsByFixingIndex,
  marketAccountsByMarket,
  allowSyntheticCrossCurrency,
}: GetOrderMarketAccountNamesParams) {
  if (!security) {
    return [];
  }

  let possibleMarkets: string[] = [];

  if (security === 'custom') {
    // Custom instruments do not have Security.Markets; callers provide the markets explicitly
    // and then reuse the same market/account filtering below.
    possibleMarkets = customAllowedMarkets ?? [];
  } else if (SecurityTypesUtils.Compound.isType(security)) {
    // For compound securities, we include markets based on the legs' securities' Markets arrays as opposed to the parent security's Markets array
    // Grab the intersection of all the leg markets and use that as our possible markets
    const marketSets = security.Legs.map(leg => new Set(securitiesBySymbol.get(leg.Symbol)?.Markets ?? []));
    const intersection = intersectManySets(marketSets) ?? new Set();
    possibleMarkets = [...intersection];
  } else {
    if (!security.Markets) {
      return [];
    }

    possibleMarkets = security.Markets;
  }

  // Online / Configured market filtering
  possibleMarkets = possibleMarkets.filter(marketName => {
    const market = marketsByName.get(marketName);
    if (!market) {
      return false;
    }
    return includeOfflineMarkets ? isMarketConfigured(market, connectionType) : isMarketOnline(market, connectionType);
  });

  // Possibly extend the market selection with rfq synthethic crossing
  if (connectionType === ConnectionType.RFQ && allowSyntheticCrosses && security !== 'custom') {
    const crossSymbol = getRFQSyntheticCrossSymbol(security);
    const crossSecurity = crossSymbol ? securitiesBySymbol.get(crossSymbol) : undefined;
    if (crossSecurity != null) {
      const crossSecurityMarkets =
        crossSecurity.Markets?.filter(marketName => {
          const market = marketsByName.get(marketName);
          return market && isMarketOnline(market, ConnectionType.RFQ);
        }) || [];
      possibleMarkets = concat(possibleMarkets, crossSecurityMarkets);
    }
  }

  // Possibly extend the market selection with unified liquidity enabled
  else if (isUnifiedLiquidityEnabled && ulResponse) {
    const unifiedLiquidityMarkets = ulResponse?.Tokens.map(m => m.Market);
    possibleMarkets = concat(possibleMarkets, unifiedLiquidityMarkets);
  }

  // remove markets based on selected strategy
  if (strategy) {
    const orderStrategy = strategiesByName.get(strategy);
    if (!orderStrategy) {
      logger.warn('Unable to resolve Strategy when producing set of Tradable Market Accounts', { strategy });
    }
    possibleMarkets = possibleMarkets.filter(m => {
      const market = marketsByName.get(m);
      if (!market || !orderStrategy) {
        return false;
      }

      // If the Market is an InternalAlgo market (the multileg market), we pass it through.
      // This selection will later be filtered out in the view component, but it needs to be present in the model's item array
      // in order to be both put on the order and correctly create a market data snapshot request
      if (market.Type === MarketTypeEnum.InternalAlgo) {
        return true;
      }

      return filterMarketsByStrategy(market, orderStrategy);
    });
  }

  // Possibly remove markets if we want to trade in counter currency, but the market does not support counter currency trading, and the user does not allow synthetic cross currency trading (a setting)
  if (usingCounterCurrency) {
    possibleMarkets = possibleMarkets.filter(m => {
      const market = marketsByName.get(m);
      const supportsNativeCounterCurrency = market?.Flags?.SupportsNativeCounterCurrency === true;
      if (supportsNativeCounterCurrency || allowSyntheticCrossCurrency) {
        return true;
      }
      return false;
    });
  }

  const isNegotiatedSpreadToFixing =
    rfqTradingProtocol === RFQTradingProtocolEnum.Negotiated && pricingMode === PricingModeEnum.SpreadToFixing;
  // Fixing-index markets are only allowed outside StF for negotiated RFQs when explicitly enabled.
  const shouldRemoveMarketsWithFixingIndex =
    rfqTradingProtocol !== RFQTradingProtocolEnum.Negotiated || !showStFMarketsInNonStFNegotiatedRFQ;

  if (isNegotiatedSpreadToFixing) {
    // For negotiated spread to fixing RFQs, only include markets associated with _every_ chosen fixing index
    // (if no fixing index is selected, this will be an empty array)
    if (!fixingIndices || fixingIndices.length === 0) {
      // No selection has been made. We don't let any markets through for selection.
      possibleMarkets = [];
    } else {
      possibleMarkets = possibleMarkets.filter(market =>
        fixingIndices.every(index => marketsByFixingIndex.get(index)?.includes(market))
      );
    }
  } else if (shouldRemoveMarketsWithFixingIndex) {
    // For streaming RFQs, and for negotiated non-spread-to-fixing RFQs without the override flag,
    // remove all markets that are associated with a fixing index.
    const marketsWithFixingIndex = [...marketsByFixingIndex.values()].flat();
    possibleMarkets = possibleMarkets.filter(market => !marketsWithFixingIndex.includes(market));
  }

  return compact(
    uniq(possibleMarkets).flatMap(market => {
      const accounts = marketAccountsByMarket.get(market) || [];
      return accounts
        .filter(ma => {
          if (rfqTradingProtocol === RFQTradingProtocolEnum.Negotiated && !ma.Capabilities?.TradingRFQNegotiated) {
            return false;
          }
          // Capture both `RFQTradingProtocolEnum.Systematic` and `undefined`
          if (rfqTradingProtocol !== RFQTradingProtocolEnum.Negotiated && ma.Capabilities?.TradingRFQNegotiated) {
            return false;
          }
          // Only include trading type accounts
          return ma.Type === MarketAccountTypeEnum.Trading;
        })
        .map(ma => ma.Name);
    })
  );
}

export type GetTradableMarketAccountNamesParams = GetOrderMarketAccountNamesParams &
  GetPermittedMarketAccountsSetParams;

/**
 * "Pure" function version of useTradableMarketAccountNames.
 *
 * Given the security and some related parameters, collects all the permitted market accounts given the sub accounts, collects
 * all the market accounts relevant, and then returns the set of tradable market accounts.
 */
export function getTradableMarketAccountNames({
  subAccountAllocations,
  combineAllMarketAccounts,
  enableAccountSegregation,
  marketAccountsList,
  marketAccountFilterIDsBySubAccountName,
  marketAccountPermissionFiltersByFilterID,

  security,
  customAllowedMarkets,
  rfqTradingProtocol,
  pricingMode,
  fixingIndices,
  showStFMarketsInNonStFNegotiatedRFQ,
  connectionType,
  isUnifiedLiquidityEnabled,
  strategy,
  usingCounterCurrency,
  includeOfflineMarkets = true,
  allowSyntheticCrosses,
  allowSyntheticCrossCurrency,

  ulResponse,
  marketsByName,
  securitiesBySymbol,
  strategiesByName,
  marketsByFixingIndex,
  marketAccountsByMarket,
}: GetTradableMarketAccountNamesParams) {
  const permittedTradableMarketAccountsSet = getPermittedMarketAccountsSet({
    subAccountAllocations,
    combineAllMarketAccounts,
    enableAccountSegregation,
    marketAccountsList,
    marketAccountFilterIDsBySubAccountName,
    marketAccountPermissionFiltersByFilterID,
  });

  const allVisibleAccounts = getOrderMarketAccountNames({
    security,
    customAllowedMarkets,
    rfqTradingProtocol,
    pricingMode,
    fixingIndices,
    showStFMarketsInNonStFNegotiatedRFQ,
    connectionType,
    isUnifiedLiquidityEnabled,
    strategy,
    usingCounterCurrency,
    includeOfflineMarkets,
    allowSyntheticCrosses,
    allowSyntheticCrossCurrency,

    marketsByName,
    ulResponse,
    securitiesBySymbol,
    strategiesByName,
    marketsByFixingIndex,
    marketAccountsByMarket,
  });

  if (permittedTradableMarketAccountsSet == null) {
    return allVisibleAccounts;
  }

  return allVisibleAccounts.filter(ma => {
    // [UI-6304] The user should be able to trade on the multileg/multileg account despite it not being permissioned via trading acct segregation
    if (ma === MULTILEG_MARKET_ACCOUNT) {
      return true;
    }
    return permittedTradableMarketAccountsSet.has(ma);
  });
}

function filterMarketsByStrategy(market: Market, orderStrategy: OrderStrategy): boolean {
  // If the Strategy supports all markets, we show all Markets except those of type Dark
  if (orderStrategy.Group === STRATEGY_GROUP.ALL) {
    if (market.Type === MarketTypeEnum.Dark) {
      return false;
    }

    return true;
  }

  // If the Strategy only supports a specific type of Market (Strategy.Group), then make sure we only show Markets of that type
  if (orderStrategy.Group !== market.Type) {
    return false;
  }

  return true;
}
